import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { DatePipe } from '@angular/common';
import { Subscription } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
//import { GridOptions, ColDef, AllModules, IDateFilterParams, ITextFilterParams, ISetFilterParams } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef,  GridApi } from 'ag-grid-community';


import { RouterLinkRendererComponent } from '../ag-grid/router-link-renderer.component';
import { ModalInfoComponent } from "../ag-grid/modal-info.component";
import { FlowIndex } from '../../models/flow.dto';
import { WorkOrderStatus } from '../../models/work-order.model';
import { FinancialAssuranceService } from '../../services/financial-assurance.service';
import { FlowInstanceService } from '../../../flow/services/flow-instance.service';
import { dateComparator, dateFilterComparator } from '../data-table/comparators/data-table.comparators';
import { DateService } from '../../services/date.service';
import { GridFormatterService } from '../../services/grid-formatter.service';
import { OrganizationComplianceDetails } from "../../models/organization.model";


@Component({
    selector: 'fa-returned-flow',
    templateUrl: './fa-returned-flow.component.html',
    standalone: false
})
export class FinancialAssuranceReturnedFlowComponent implements OnInit, OnDestroy {

    @Input() isInternal: boolean = false;
    @Input() public orgId: string;



    @Input() complianceData: OrganizationComplianceDetails[];
    @Input() gridAriaLabel: string;

    columnExternalApplicationNumber: ColDef = {
        headerName: 'Application Number',
        headerTooltip: 'Application Number',
        headerClass: "wrapped",
        field: 'flowIdentifier',
        cellRendererSelector: (params) => {
            if (params.data.onBehalf) {
                return {
                    component: ModalInfoComponent,
                    params: {
                        modalText: (data: any) => { return `Application ${data.flowIdentifier} is a paper submission that must be completed by ODNR.`; },
                        text: (data: any) => { return data.flowIdentifier; }
                    }
                }
            } else {
                return {
                    component: RouterLinkRendererComponent,
                    params: {
                        textField: 'flowIdentifier',
                        flowIdField: 'flowId'
                    }
                }
            }
        },
        tooltipField: 'flowIdentifier',
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        maxWidth: 130
    }

    columnInternalApplicationNumber: ColDef = {
        cellRenderer: RouterLinkRendererComponent,
        headerClass: "wrapped",
        cellRendererParams: {
            textField: 'flowIdentifier',
            openInNewWindow: false,
            url: (data: FlowIndex) => {
                const prefix = (data.onBehalf) ? "/flow/review-on-behalf" : "/flow/review";
                return `${prefix}/${data.flowId}`;
            }
        },
        headerName: 'Application Number',
        headerTooltip: 'Application Number',
        field: 'flowIdentifier',
        tooltipField: 'flowIdentifier',
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
        maxWidth: 175
    }

    columnInternalCompanyName: ColDef = {
        headerClass: "wrapped",
        headerName: 'Company',
        headerTooltip: 'Company',
        field: 'companyName',
        tooltipField: 'companyName',
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
    }

    columnDateCreated: ColDef = {
        headerClass: "wrapped",
        headerName: 'Submitted',
        headerTooltip: 'Submitted',
        tooltipValueGetter: this.formatterService.formatDate('dateSubmitted'),
        field: 'dateSubmitted',
        valueGetter: this.formatterService.formatDate('dateSubmitted'),
        comparator: dateComparator,
        filter: "agDateColumnFilter",
        filterParams: this.formatterService.defaultDateFilterParams,
        maxWidth: 150
    };

    columnCreatedBy: ColDef = {
        headerClass: "wrapped",
        headerName: 'Created By',
        headerTooltip: 'Created By',
        tooltipField: 'userName',
        field: 'userName',
        filter: "agTextColumnFilter",
        filterParams: this.formatterService.defaultTextFilterParams,
    };

    columnFlowName: ColDef = {
        headerClass: "wrapped",
        headerName: 'Type',
        headerTooltip: 'Type',
        tooltipField: 'name',
        field: 'name',
        maxWidth: 120
    };

    ownerTypeColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Owner Type",
        headerTooltip: "Owner Type",
        tooltipValueGetter: this.formatOwnerType(),
        valueGetter: this.formatOwnerType(),
        minWidth: 110
    }

    data: FlowIndex[];
	gridOptions: GridOptions;
	gridApi?: GridApi;
    subscriptions: Subscription = new Subscription();

    internalColumns = [
        this.columnInternalApplicationNumber,
        this.columnInternalCompanyName,
        this.ownerTypeColumn,
        this.columnDateCreated,
        this.columnFlowName
    ];

    externalColumns = [
        this.columnExternalApplicationNumber,
        this.columnDateCreated,
        this.columnCreatedBy,
        this.columnFlowName,
    ];

    statusEnum = WorkOrderStatus;
    templateIds = FinancialAssuranceService.TemplateIds;

    constructor(
        private formatterService: GridFormatterService,
        public flowInstanceService: FlowInstanceService,
        private router: Router
    ) {
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    ngOnInit() {

        this.refreshData();

        this.gridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns((this.isInternal ? this.internalColumns : this.externalColumns));
        this.gridOptions.isExternalFilterPresent = () => false;
        this.gridOptions.headerHeight = 50;
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.subscriptions.unsubscribe();
        });
    }

    formatOwnerType(): any {
        return (obj) => {
            let returnValue: string = "";
            if (obj.data != null && obj.data["organizationId"] != null && this.complianceData != null) {
                let organizationId = obj.data["organizationId"];
                let compliance = this.complianceData.find(c => c.orgId == organizationId);
                if (compliance != null) {
                    if (compliance.isBrineHauler != null) {
                        if (compliance.isBrineHauler == true) returnValue = "Brine Hauler";
                    }
                    if (compliance.isWellOwner != null) {
                        if (compliance.isWellOwner == true) {
                            if (returnValue.length > 0) {
                                returnValue += ", ";
                            }
                            returnValue += "Well Owner";
                        }
                    }
                }

            }
            if (returnValue.length === 0) returnValue = "N/A";
            return returnValue;
        }
    }

    refreshData(): void {
        this.subscriptions.add(this.flowInstanceService.getReturnedIncompleteApplication('FinancialAssuranceNotSubmittedFlows', true, this.orgId).subscribe(
            data => {
                data.forEach(d => {
                    if (!d.dateSubmitted) d.dateSubmitted = d.createdDate;
                })
                this.data = data.sort((a, b) => a.dateSubmitted > b.dateSubmitted ? 1 : -1);
                this.resizeColumns();
            },
            error => { console.log(error); alert("ERROR Fetching Returned Financial Assurance Applications"); }
        ));
    }

    resizeColumns(): void {
        if (this.gridApi) {
            setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
        }
    }

}

